import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, Button, Input, Select, Textarea, Field, Stat, StatusBadge } from "@/components/UI";
import { Plus, Trash, Coins, Trophy, ChartLine, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { GroupedTable, MonthlySummaryBar, PerformerField, thisMonthKey } from "@/components/VetTable";
import { today } from "./helpers";
import { PregnancyEditModal } from "./PregnancyEditModal";


export function Pregnancy() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ animal_type: "inek", checked_count: 1, pregnant_count: 0, empty_count: 0, suspicious_count: 0, date: today(), vet_name: "", notes: "" });
  const load = () => api.get("/vet/pregnancy-checks").then(r => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const checked = Number(form.checked_count || 0);
  const preg = Number(form.pregnant_count || 0);
  const empty = Number(form.empty_count || 0);
  const susp = Number(form.suspicious_count || 0);
  const sum = preg + empty + susp;
  const remaining = checked - sum;
  const overLimit = sum > checked;

  const submit = async (e) => {
    e.preventDefault();
    if (overLimit) return toast.error("Sonuçların toplamı bakılan sayısını aşamaz");
    const body = {
      ...form,
      vet_name: form.vet_name?.trim() || user?.name || "",
      checked_count: checked, pregnant_count: preg, empty_count: empty, suspicious_count: susp,
    };
    try {
      await api.post("/vet/pregnancy-checks", body);
      toast.success(`${checked} ${form.animal_type} için kayıt yapıldı`);
      setForm({ ...form, checked_count: 1, pregnant_count: 0, empty_count: 0, suspicious_count: 0, notes: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu gebelik kontrol kaydını silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/vet/pregnancy-checks/${id}`);
      toast.success("Silindi");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <Card>
        <h3 className="heading text-lg mb-3">Gebelik Kontrol Kaydı</h3>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Hayvan Tipi *">
              <Select data-testid="preg-animal-type" value={form.animal_type} onChange={e => setForm({ ...form, animal_type: e.target.value })}>
                <option value="inek">İnek</option>
                <option value="düve">Düve</option>
              </Select>
            </Field>
            <Field label="Bakılan Adet *">
              <Input data-testid="preg-checked" type="number" min="1" required value={form.checked_count} onChange={e => setForm({ ...form, checked_count: e.target.value })} />
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Gebe (+)">
              <Input data-testid="preg-pregnant" type="number" min="0" value={form.pregnant_count} onChange={e => setForm({ ...form, pregnant_count: e.target.value })} />
            </Field>
            <Field label="Boş (GD-)">
              <Input data-testid="preg-empty" type="number" min="0" value={form.empty_count} onChange={e => setForm({ ...form, empty_count: e.target.value })} />
            </Field>
            <Field label="Şüpheli">
              <Input data-testid="preg-susp" type="number" min="0" value={form.suspicious_count} onChange={e => setForm({ ...form, suspicious_count: e.target.value })} />
            </Field>
          </div>

          {checked > 0 && (
            <div className={`text-xs px-3 py-2 rounded-md border ${overLimit ? "bg-[#fbeee9] border-[#f2d4cb] text-[#8a3a2b]" : "bg-[#E4EEE6] border-[#c2d6c8] text-[#2D4A36]"}`} data-testid="preg-preview">
              {overLimit ? (
                <>⚠️ Sonuçlar toplamı ({sum}) bakılan sayısını ({checked}) aşıyor</>
              ) : (
                <>✓ {checked} {form.animal_type} için: <b>{preg}</b> gebe, <b>{empty}</b> boş, <b>{susp}</b> şüpheli
                {remaining > 0 && <span className="text-[#5e675e]"> · {remaining} kaydedilmemiş</span>}
                {checked > 0 && <div className="mt-0.5">Gebelik oranı: <b>%{((preg / checked) * 100).toFixed(1)}</b></div>}
                </>
              )}
            </div>
          )}

          <Field label="Tarih *"><Input type="date" data-testid="preg-date" required value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></Field>
          <PerformerField label="Kontrol Eden (İşlemi Yapan)" user={user} value={form.vet_name}
            onChange={v => setForm({ ...form, vet_name: v })} testId="preg-vet" />
          <Field label="Notlar"><Textarea data-testid="preg-notes" rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></Field>
          <Button data-testid="preg-submit" type="submit" disabled={overLimit}><Plus size={14} className="inline mr-1" /> Kaydet</Button>
        </form>
      </Card>
      <GroupedTable
        className="lg:col-span-2"
        testId="preg"
        items={items}
        emptyText="Henüz kayıt yok"
        summary={<MonthlySummaryBar testId="preg-summary" stats={(() => {
          const mk = thisMonthKey();
          const mo = items.filter(x => (x.date || "").slice(0, 7) === mk);
          const checkedT = mo.reduce((a, x) => a + Number(x.checked_count || 0), 0);
          const pregT = mo.reduce((a, x) => a + Number(x.pregnant_count || 0), 0);
          const rate = checkedT ? ((pregT / checkedT) * 100).toFixed(1) : "0";
          return [
            { label: "Bakılan", value: checkedT },
            { label: "Gebe", value: pregT },
            { label: "Ort. oran", value: `%${rate}`, tone: "text-[#2D4A36]" },
            { label: "Kayıt", value: mo.length },
          ];
        })()} />}
        dayMeta={(entries) => `${entries.reduce((a, x) => a + Number(x.checked_count || 0), 0)} bakılan · ${entries.reduce((a, x) => a + Number(x.pregnant_count || 0), 0)} gebe`}
        columns={[
          { header: "Hayvan", cell: x => <span className="capitalize font-semibold">{x.animal_type || "—"}</span> },
          { header: "Bakılan", thClass: "text-right", tdClass: "text-right tabnum", cell: x => x.checked_count ?? 0 },
          { header: "Gebe (+)", thClass: "text-right", tdClass: "text-right tabnum text-[#2D4A36] font-semibold", cell: x => x.pregnant_count ?? 0 },
          { header: "Boş (GD-)", thClass: "text-right", tdClass: "text-right tabnum text-[#C45B45] font-semibold", cell: x => x.empty_count ?? 0 },
          { header: "Şüpheli", thClass: "text-right", tdClass: "text-right tabnum text-[#8B6914]", cell: x => x.suspicious_count ?? 0 },
          { header: "Oran", thClass: "text-right", tdClass: "text-right tabnum", cell: x => `%${x.checked_count ? ((x.pregnant_count / x.checked_count) * 100).toFixed(1) : "—"}` },
          { header: "Veteriner", cell: x => x.vet_name || "—" },
          { header: "Notlar", tdClass: "text-[#5e675e] text-sm max-w-[220px] whitespace-pre-wrap break-words", cell: x => x.notes || "—" },
          { header: "", thClass: "text-right", tdClass: "text-right whitespace-nowrap", cell: x => (
            <div className="inline-flex items-center gap-1">
              <button onClick={() => setEditing(x)} data-testid={`preg-edit-${x.id}`} title="Düzenle" className="text-[#5A6E5F] hover:text-[#2D4A36] p-1"><PencilSimple size={15} /></button>
              <button onClick={() => remove(x.id)} data-testid={`preg-del-${x.id}`} title="Kaydı sil" className="text-[#C45B45] hover:text-[#8a3a2b] p-1"><Trash size={15} /></button>
            </div>
          ) },
        ]}
      />
      {editing && (
        <PregnancyEditModal item={editing} user={user}
          onClose={() => setEditing(null)} onDone={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

